/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

export interface Chunk {
  id: string
  projectId: string
  filePath: string
  content: string
  startLine: number
  endLine: number
  tokens: number
  hash: string
}

/**
 * Simple token counter (approximation: ~4 chars per token)
 */
function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4)
}

/**
 * Generate hash for content
 */
function hashContent(content: string): string {
  let hash = 0
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return hash.toString(36)
}

/**
 * Split file content into semantic chunks
 * Target: 600-800 tokens per chunk
 */
export function chunkFile(
  projectId: string,
  filePath: string,
  content: string,
  options: {
    minTokens?: number
    maxTokens?: number
    overlapLines?: number
  } = {}
): Chunk[] {
  const minTokens = options.minTokens || 600
  const maxTokens = options.maxTokens || 800
  const overlapLines = options.overlapLines || 2

  const chunks: Chunk[] = []
  const lines = content.split('\n')
  
  let currentChunk: string[] = []
  let currentStartLine = 1
  let chunkIndex = 0

  for (let i = 0; i < lines.length; i++) {
    currentChunk.push(lines[i])
    const chunkText = currentChunk.join('\n')
    const tokens = estimateTokens(chunkText)

    // If we've reached max tokens or end of file
    if (tokens >= maxTokens || i === lines.length - 1) {
      // Only create chunk if it meets minimum size
      if (tokens >= minTokens || i === lines.length - 1) {
        const chunk: Chunk = {
          id: `${projectId}:${filePath}:${chunkIndex}`,
          projectId,
          filePath,
          content: chunkText,
          startLine: currentStartLine,
          endLine: i + 1,
          tokens,
          hash: hashContent(chunkText)
        }
        chunks.push(chunk)
        chunkIndex++

        // Start new chunk with overlap
        currentChunk = lines.slice(Math.max(0, i - overlapLines + 1), i + 1)
        currentStartLine = Math.max(1, i - overlapLines + 2)
      }
    }
  }

  return chunks
}

/**
 * Chunk all files in a project
 */
export function chunkProject(
  projectId: string,
  files: Record<string, string>,
  options?: {
    minTokens?: number
    maxTokens?: number
    overlapLines?: number
    excludePatterns?: RegExp[]
  }
): Chunk[] {
  const excludePatterns = options?.excludePatterns || [
    /node_modules/,
    /\.min\./,
    /\.map$/,
    /package-lock\.json$/,
    /yarn\.lock$/,
    /\.git/,
    /dist\//,
    /build\//,
  ]

  const allChunks: Chunk[] = []

  for (const [filePath, content] of Object.entries(files)) {
    // Skip excluded files
    if (excludePatterns.some(pattern => pattern.test(filePath))) {
      continue
    }

    // Skip binary files and very large files
    if (content.length > 1_000_000) {
      console.warn(`Skipping large file: ${filePath} (${content.length} bytes)`)
      continue
    }

    const fileChunks = chunkFile(projectId, filePath, content, options)
    allChunks.push(...fileChunks)
  }

  return allChunks
}

/**
 * Get chunk by ID
 */
export function getChunkById(chunks: Chunk[], chunkId: string): Chunk | undefined {
  return chunks.find(c => c.id === chunkId)
}

/**
 * Filter chunks by file path
 */
export function filterChunksByPath(chunks: Chunk[], pathPattern: string | RegExp): Chunk[] {
  const pattern = typeof pathPattern === 'string' 
    ? new RegExp(pathPattern, 'i')
    : pathPattern
  
  return chunks.filter(c => pattern.test(c.filePath))
}

